import { IoSearch } from "react-icons/io5";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { FaUsers } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import ClipLoader from "react-spinners/ClipLoader";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { groupSearch } from "../../redux/ApiSlice/SearchSlice";
import { groupJoin } from "../../redux/ApiSlice/Group/GroupSlice";

const GroupListView = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [localResults, setLocalResults] = useState([]);
    const searchResults = useSelector((state) => state?.SearchItems?.groupSearchResult ?? []);

    useEffect(() => {
        setLocalResults(searchResults);
    }, [searchResults]);

    const fetchGroupSearchResults = useCallback(async () => {
        await dispatch(groupSearch(query));
        setLoading(false);
    }, [dispatch, query]);

    // Debounce: wait 400ms after the user stops typing before firing the API call
    useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => {
            fetchGroupSearchResults();
        }, 400);
        return () => clearTimeout(timer);
    }, [fetchGroupSearchResults]);

    const handleSearchChange = (e) => {
        setQuery(e.target.value);
    };

    const handleJoinGroup = async (groupId) => {
        try {
            const response = await dispatch(groupJoin(groupId));
            if (response?.payload?.message === "Join request has been sent to the group owner.") {
                setLocalResults((prev) =>
                    prev.map((data) =>
                        data.id === groupId ? { ...data, join_request_status: "pending" } : data
                    )
                );
            } else {
                fetchGroupSearchResults();
            }
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Fragment>
            <div className="sticky top-0">
                <div className="h-12 px-2 flex justify-between items-center bg-[#BDE6FF] rounded-b-xl relative">
                    <IoMdArrowBack
                        onClick={() => navigate(-1)}
                        className="text-[24px] text-black cursor-pointer"
                    />
                    <p className="font-bold text-[18px] mr-4 text-black ">Groups</p>
                    <div
                        className="relative"
                        onClick={() => navigate("/createnewgroup")}
                    >
                        <FaUsers className="text-[24px] text-black cursor-pointer" />
                        <MdAdd className="absolute top-[16px] -right-[3px] text-[12px] text-black cursor-pointer" />
                    </div>
                </div>
                <div className="bg-white dark:bg-[#1E1D21] py-2 border-b border-gray-400/50 dark:border-gray-800/50">
                    <div className="relative mx-2">
                        <input
                            type="text"
                            placeholder="Search groups..."
                            value={query}
                            onChange={handleSearchChange}
                            className="w-full py-[6px] pl-1.5 pr-8 text-[14px] bg-white dark:bg-[#333333] text-black dark:text-white border border-gray-400/50 dark:border-gray-800/50 rounded-md focus:outline-none focus:ring-[1px] focus:ring-blue-600"
                        />
                        <IoSearch className="absolute text-[18px] right-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-white" />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-[50vh]">
                    <ClipLoader
                        size={30}
                        color={"#CAD2C5"}
                    />
                </div>
            ) : (
                <div className="">
                    {localResults.length > 0 ? (
                        localResults.map((data) => (
                            <div key={data.id} className="border-b border-gray-400/50 dark:border-gray-800/50 p-2">
                                <div className="grid grid-cols-12 items-start">
                                    <div className="col-span-2">
                                        <img
                                            alt={data.group_name}
                                            src={data.group_profile_picture}
                                            onClick={() => navigate(`/group/${data.id}`)}
                                            className="w-12 h-12 rounded-md object-cover border border-gray-300 dark:border-gray-800 cursor-pointer"
                                        />
                                    </div>
                                    <div className="col-span-7 flex flex-col">
                                        <p
                                            onClick={() => navigate(`/group/${data.id}`)}
                                            className="font-semibold text-[11px] text-black dark:text-white"
                                        >
                                            {data.group_name}
                                        </p>
                                        <p
                                            onClick={() => navigate(`/group/${data.id}`)}
                                            className="font-semibold text-[11px] text-black dark:text-white line-clamp-1"
                                        >
                                            {data.description}
                                        </p>
                                        <p className="font-medium text-[10px] text-gray-500 dark:text-gray-600">
                                            {data?.member_count} members
                                        </p>
                                    </div>
                                    <div className="col-span-3 flex flex-col justify-between items-end">
                                        {data.join_request_status !== "pending" ? (
                                            data?.user_joined ? (
                                                <button
                                                    className="bg-[#BDE6FF] text-gray-800 bg-opacity-40 dark:bg-opacity-40 rounded-md text-[11px] p-1 w-full font-semibold"
                                                    onClick={() => navigate(`/group/${data.id}`)}
                                                >
                                                    Joined
                                                </button>
                                            ) : (
                                                <button
                                                    className="bg-[#BDE6FF] text-black rounded-md text-[11px] p-1 w-full font-semibold"
                                                    onClick={() => handleJoinGroup(data.id)}
                                                >
                                                    Join
                                                </button>
                                            )
                                        ) : (
                                            <button
                                                className="bg-[#BDE6FF] text-gray-800 rounded-md text-[11px] p-1 w-full font-semibold dark:text-white"
                                                onClick={() => navigate(`/group/${data.id}`)}
                                            >
                                                Requested
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="px-2 pt-10 text-[14px] text-center text-gray-500 dark:text-gray-600">
                            No groups found
                        </p>
                    )}
                </div>
            )}
        </Fragment>
    );
};

export default GroupListView;